import { ModalManager } from './manager';
import { setLocale, withLocale, getLocale, stripLocale, t, AVAILABLE_LOCALES } from '../i18n';
import type { Locale } from '../i18n/types';

/**
 * Language picker. All 5 locales now ship full translated UI bundles + 192
 * country articles each. Selecting a locale updates the i18n state AND
 * navigates to the locale-prefixed equivalent of the current path so the
 * router re-renders the page in the chosen language.
 */

interface LangOption {
  code: Locale;
  label: string;
  available: boolean;
}

/**
 * Endonyms — each language named in itself, which is what a speaker scans for.
 *
 * Derived from AVAILABLE_LOCALES rather than listed independently: zh shipped
 * on 2026-07-29 with its catalog, its routes and its hreflang all live, and was
 * still invisible here because this array was the one place that had not
 * learned about it. A locale nobody can select is a locale that does not exist.
 */
const ENDONYM: Record<Locale, string> = {
  en: 'English',
  ru: 'Русский',
  es: 'Español',
  pt: 'Português (Brasil)',
  tr: 'Türkçe',
  zh: '简体中文',
};

const LANGS: LangOption[] = AVAILABLE_LOCALES.map((code) => ({
  code,
  label: ENDONYM[code],
  available: true,
}));

const TITLE_ID = 'lang-picker-title';

export function openLangPicker(): void {
  ModalManager.open({
    id: 'lang-picker',
    panelClass: 'lang-picker',
    labelledBy: TITLE_ID,
    render() {
      const current = getLocale();
      return `
<h2 id="${TITLE_ID}" class="lang-picker__title">${t('lang.choose')}</h2>
<ul class="lang-picker__list">
  ${LANGS.map(
    (l) => `<li>
    <button class="lang-option ${l.code === current ? 'lang-option--current' : ''}" data-lang="${l.code}">
      <span class="lang-option__label">${l.label}</span>
      ${l.code === current ? `<span class="lang-option__current">${t('lang.active')}</span>` : ''}
    </button>
  </li>`,
  ).join('')}
</ul>
<p class="lang-picker__note">${t('lang.machineTranslatedNote')}</p>`;
    },
    onMount(panel) {
      const onClick = (e: Event): void => {
        const btn = (e.target as Element | null)?.closest<HTMLButtonElement>('[data-lang]');
        if (!btn || btn.disabled) return;
        const code = btn.dataset.lang as Locale | undefined;
        if (!code) return;
        if (code === getLocale()) {
          ModalManager.close('lang-picker');
          return;
        }
        // Bundle-split (2026-05-08): non-EN article catalogs are lazy.
        // Preload the new locale BEFORE we re-render the route, otherwise
        // the first paint would show EN copy until the chunk lands. EN
        // short-circuits inside ensureArticlesLoaded.
        void (async () => {
          const { ensureArticlesLoaded } = await import('../data/articles');
          await ensureArticlesLoaded(code);
          // Switch locale, then push locale-prefixed equivalent of current path.
          const canonical = stripLocale(window.location.pathname);
          setLocale(code);
          const target = withLocale(canonical, code);
          window.history.pushState({}, '', target);
          // Trigger router re-resolve via popstate dispatch — keeps a single code-path.
          window.dispatchEvent(new PopStateEvent('popstate'));
          ModalManager.close('lang-picker');
        })();
      };
      panel.addEventListener('click', onClick);
      return () => panel.removeEventListener('click', onClick);
    },
  });
}
